import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["children", "className"],
  _excluded2 = ["item"],
  _excluded3 = ["items"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { PureComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Plugin } from 'prosemirror-state';
import { Menu, Dropdown, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import cx from 'classnames';
import './Menu.scss';
import { setCellAttrs, findParentNodeOfType } from 'prosemirror-utils';
import isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import { SEPERATOR_ITEM, getDropdownItem, getSeparator } from '../../utils/menu';
const COLOR_ITEM_OPTIONS = ['#1c2534', '#bba8f6', '#2edcea', '#c06868', '#fc9a61', '#fddc4a', '#acd942', '#648de6', '#eeeeee', '#f9dbf4', '#d6effb', '#f9e3d7', '#ccf4e9', '#fbeabc', '#d8f5c8', '#d2dffc'];
const updateItemActive = (item, view) => {
  const newItem = _objectSpread(_objectSpread({}, item), {}, {
    enabled: true
  });
  newItem.hidden = newItem.isHidden ? newItem.isHidden(view.state) : false;
  if (newItem.type === 'group') {
    newItem.items = newItem.items.map(subItem => updateItemActive(subItem, view));
    newItem.enabled = newItem.items.some(subItem => subItem.enabled);
    if (!newItem.isHidden) {
      newItem.hidden = !newItem.enabled;
    }
  }
  if (newItem.type === 'dropdown') {
    newItem.items = newItem.items.map(subItem => updateItemActive(subItem, view));
    newItem.enabled = newItem.items.some(subItem => subItem.enabled);
    newItem.hidden = newItem.showOnDisabled ? false : !newItem.enabled;
  }
  if (newItem.isActive) {
    newItem.active = newItem.isActive(view.state, null, view);
  }
  if (newItem.isEnabled) {
    newItem.enabled = newItem.isEnabled(view.state, null, view);
  } else if (newItem.run) {
    newItem.enabled = newItem.run(view.state, null, view);
  }
  return newItem;
};
const ColorDropdownItem = _ref => {
  let {
    item,
    onClick
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Dropdown, {
    overlay: /*#__PURE__*/React.createElement("div", {
      className: "colorPalettecontainer"
    }, COLOR_ITEM_OPTIONS.map(color => /*#__PURE__*/React.createElement("div", {
      className: "childs",
      key: color,
      style: {
        backgroundColor: color
      },
      onClick: onClick(item, color)
    })))
  }, /*#__PURE__*/React.createElement("div", null, item.markType === 'cell background' ? /*#__PURE__*/React.createElement("span", null, item.title) : /*#__PURE__*/React.createElement("span", {
    className: item.icon
  }, /*#__PURE__*/React.createElement("span", {
    className: "path1"
  }), /*#__PURE__*/React.createElement("span", {
    className: "path2"
  })))));
};
const DropdownMenuItem = _ref2 => {
  let {
    children,
    id,
    items,
    view,
    onClick,
    onMouseDown,
    onClickColor
  } = _ref2;
  const renderMenuItem = item => /*#__PURE__*/React.createElement(MenuItem, {
    item: item,
    view: view,
    tooltipProps: {
      placement: 'right'
    },
    onClick: onClick,
    onMouseDown: onMouseDown,
    onClickColor: onClickColor
  });
  const selectedKeys = useMemo(() => items.reduce((result, item) => item.active ? [...result, item.id] : result, []), [items]);
  return /*#__PURE__*/React.createElement(Dropdown, {
    overlayClassName: "tap-editor-menudropdown",
    overlay: /*#__PURE__*/React.createElement(Menu, {
      style: {
        display: 'flex',
        flexDirection: id === 'align-menu' ? "row" : 'column'
      },
      selectedKeys: selectedKeys
    }, items.map(item => !item.hidden && (item.type === 'dropdown' ? /*#__PURE__*/React.createElement(Menu.SubMenu, {
      title: item === null || item === void 0 ? void 0 : item.label,
      key: item.id,
      popupClassName: "tap-editor-menu-submenu"
    }, item.items.map(subItem => /*#__PURE__*/React.createElement(Menu.Item, {
      key: subItem.id,
      active: subItem.active,
      disabled: !subItem.enabled || subItem.type === 'separator'
    }, renderMenuItem(subItem)))) : /*#__PURE__*/React.createElement(Menu.Item, {
      key: item.id,
      active: item.active,
      disabled: !item.enabled || item.type === 'separator',
      className: item.type === 'separator' ? 'tap-editor-separatormenuitem' : ''
    }, renderMenuItem(item)))))
  }, children);
};
const MenuItem = _ref3 => {
  let {
    item,
    view,
    tooltipProps = {},
    onClick,
    onClickColor,
    onMouseDown
  } = _ref3;
  const defaultMenuOnClick = useCallback(() => view.focus(), [view]);
  const MenuItemWrapper = useMemo(() => _ref4 => {
    let {
        children,
        className
      } = _ref4,
      props = _objectWithoutProperties(_ref4, _excluded);
    return /*#__PURE__*/React.createElement("div", _extends({}, props, {
      className: cx('tap-editor-menuitem', className, {
        disabled: !item.enabled,
        active: item.active
      }),
      key: item.id,
      role: "button",
      tabIndex: "0",
      style: item.style || {},
      onClick: item.enabled && item.run ? onClick(item.run) : defaultMenuOnClick,
      onMouseDown: onMouseDown
    }), children);
  }, [item.active, item.enabled, item.id, item.run, item.style, onClick, onMouseDown, defaultMenuOnClick]);
  if (!item || item.hidden) return null;
  if (item.type === 'separator') {
    return /*#__PURE__*/React.createElement("div", {
      className: "tap-editor-menuitem seperator",
      key: item.id
    });
  }
  if (item.type === 'more') {
    return /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: '5px',
        marginTop: '10px'
      },
      className: item.icon
    });
  }
  let content = '';
  if (item.type === 'dropdown') {
    content = /*#__PURE__*/React.createElement(DropdownMenuItem, {
      id: item.id,
      items: item.items,
      view: view,
      onClick: onClick,
      onMouseDown: onMouseDown,
      onClickColor: onClickColor
    }, /*#__PURE__*/React.createElement(MenuItemWrapper, null, item.icon ? /*#__PURE__*/React.createElement("span", {
      className: item.icon
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, item.label, " ", /*#__PURE__*/React.createElement(DownOutlined, null))));
  } else {
    content = /*#__PURE__*/React.createElement(MenuItemWrapper, null, item.type === 'icon' && /*#__PURE__*/React.createElement("span", {
      className: cx('menuitem-icon', item.icon)
    }, /*#__PURE__*/React.createElement("span", {
      className: "path1"
    }), /*#__PURE__*/React.createElement("span", {
      className: "path2"
    })), item.type === 'label' && /*#__PURE__*/React.createElement("span", null, item.label), item.type === 'colorPalette' && /*#__PURE__*/React.createElement(ColorDropdownItem, {
      item: item,
      onClick: onClickColor
    }), item.render && item.render(view));
  }
  return content && item.title ? /*#__PURE__*/React.createElement(Tooltip, _extends({
    title: item.title,
    placement: "bottom",
    color: "white"
  }, tooltipProps, item.tooltipProps || {}), content) : content;
};
const MenuGroup = _ref5 => {
  let {
      item
    } = _ref5,
    props = _objectWithoutProperties(_ref5, _excluded2);
  const [filteredItems, setFilteredItems] = useState([]);
  const [moreItems, setMoreItems] = useState([]);
  const {
    items,
    maxItems
  } = useMemo(() => item, [item]);
  useEffect(() => {
    if (maxItems >= 0) {
      setFilteredItems(items.slice(0, maxItems));
      setMoreItems(items.slice(maxItems));
      return;
    }
    setFilteredItems(items);
    setMoreItems([]);
  }, [items, maxItems]);
  const moreMenuItem = useMemo(() => moreItems.length > 0 ? updateItemActive(getDropdownItem("".concat(item.id, "-more"), '', moreItems, 'tap-icon-editor-more'), props.view) : null, [item, moreItems, props.view]);
  return /*#__PURE__*/React.createElement("div", {
    className: "tap-editor-menugroup"
  }, filteredItems.map(menuItem => /*#__PURE__*/React.createElement(MenuItem, _extends({
    key: menuItem.id,
    item: menuItem
  }, props))), moreMenuItem && /*#__PURE__*/React.createElement(MenuItem, _extends({
    item: moreMenuItem
  }, props)), !item.hideSeperatorAtEnd && filteredItems.length > 1 && /*#__PURE__*/React.createElement(MenuItem, _extends({
    item: SEPERATOR_ITEM
  }, props)));
};
const MenuGroupList = _ref6 => {
  let {
      items
    } = _ref6,
    props = _objectWithoutProperties(_ref6, _excluded3);
  const [renderInfo, setRenderInfo] = useState({
    resize: true,
    items: items || []
  });
  const containerRef = useRef();
  useEffect(() => {
    setRenderInfo({
      resize: true,
      items: items || []
    });
  }, [items]);
  const resizeLayout = useCallback(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const {
      width: containerWidth
    } = containerEl.getBoundingClientRect();
    const moreIconWidth = 25;
    let renderedWidth = moreIconWidth; // 25 is the width required to show more icon
    let maxIndex = 0;
    let lastGroupMaxIndex = -1;
    const totalGroups = containerEl.childElementCount;
    const groupEls = containerEl.childNodes;
    for (let index = 0; index < totalGroups; index++) {
      const groupEl = groupEls[index];
      const {
        width: groupWidth
      } = groupEl.getBoundingClientRect();
      if (containerWidth >= renderedWidth + groupWidth) {
        renderedWidth += groupWidth;
        maxIndex = index;
      } else {
        let remainingWidth = containerWidth - renderedWidth;
        const totalGroupChildren = groupEl.childElementCount;
        const menuItemEls = groupEl.childNodes;
        for (let itemIndex = 0; itemIndex < totalGroupChildren; itemIndex++) {
          const menuItemEl = menuItemEls[itemIndex];
          const {
            width: itemWidth
          } = menuItemEl.getBoundingClientRect();
          const isLastGroupAndLastItem = itemIndex === totalGroupChildren - 1 && index === totalGroups - 1;
          if (remainingWidth >= itemWidth || isLastGroupAndLastItem && remainingWidth + moreIconWidth >= itemWidth) {
            lastGroupMaxIndex = itemIndex;
            remainingWidth -= itemWidth;
            if (isLastGroupAndLastItem) {
              maxIndex = index;
            }
          } else {
            break;
          }
        }
        break;
      }
    }
    if (maxIndex < items.length) {
      const nextHiddenGroupIndex = maxIndex + 1;
      const newFilteredItems = items.slice(0, nextHiddenGroupIndex);

      // If there is hidden group/groups next
      if (lastGroupMaxIndex >= 0 || items.length > nextHiddenGroupIndex) {
        // Process immediate next group and concat other group items
        const nextGroupObj = items[nextHiddenGroupIndex];
        newFilteredItems.push(_objectSpread(_objectSpread({}, nextGroupObj), {}, {
          hideSeperatorAtEnd: true,
          items: [].concat(...items.slice(nextHiddenGroupIndex).map(group => [...group.items, getSeparator("".concat(group.id, "-resize-more"))])),
          maxItems: lastGroupMaxIndex + 1
        }));
      }
      setRenderInfo({
        resize: false,
        items: newFilteredItems
      });
    }
  }, [items]);
  const resizeLayoutDebounced = useCallback(_debounce(resizeLayout, 100), [items]);
  const onWindowResize = useCallback(() => {
    setRenderInfo({
      resize: false,
      items: items || []
    });
    resizeLayoutDebounced();
  }, [items, resizeLayoutDebounced]);
  useEffect(() => {
    if (renderInfo.resize) {
      resizeLayoutDebounced();
    }
  }, [renderInfo, resizeLayoutDebounced]);
  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [onWindowResize]);
  return /*#__PURE__*/React.createElement("div", {
    className: "tap-editor-menu",
    ref: containerRef
  }, renderInfo.items.map(item => /*#__PURE__*/React.createElement(MenuGroup, _extends({
    key: item.id,
    item: item
  }, props))));
};
export class MenuList extends PureComponent {
  constructor() {
    super(...arguments);
    this.onClickMenuItem = run => e => {
      e.preventDefault();
      const {
        view
      } = this.props;
      run(view.state, view.dispatch, view, e);
      view.focus();
    };
    this.onPaletteClicked = (item, color) => e => {
      e.preventDefault();
      const {
        view
      } = this.props;
      const {
        selection,
        tr
      } = view.state;
      const {
        from,
        to
      } = selection;
      if (item.markType === 'cell background') {
        const {
          schema: {
            nodes: {
              table_header: headerNode,
              table_cell: cellNode
            }
          }
        } = view.state;
        const header = findParentNodeOfType(headerNode)(selection);
        const cell = findParentNodeOfType(cellNode)(selection);
        view.dispatch(setCellAttrs(header || cell, {
          background: color
        })(tr));
      } else if (from === to) {
        view.dispatch(tr.addStoredMark(item.markType.create({
          color
        })));
      } else {
        view.dispatch(tr.addMark(from, to, item.markType.create({
          color
        })));
      }
      view.focus();
    };
    this.handleMouseDown = event => {
      event.preventDefault();
    };
  }
  render() {
    const {
      view,
      items
    } = this.props;
    return /*#__PURE__*/React.createElement(MenuGroupList, {
      items: items,
      view: view,
      onClick: this.onClickMenuItem,
      onClickColor: this.onPaletteClicked,
      onMouseDown: this.handleMouseDown
    });
  }
}
MenuList.propTypes = {
  view: PropTypes.object.isRequired,
  items: PropTypes.array
};
MenuList.defaultProps = {
  items: []
};
class MenuView {
  constructor(wrapperEl, items, view) {
    this.wrapperEl = wrapperEl;
    this.items = items;
    this.view = view;
    this.menuRef = /*#__PURE__*/React.createRef();
    this.update();
  }
  update() {
    const prevItems = this.items;
    const newItems = this.items.map(item => {
      const newItem = updateItemActive(item, this.view);
      return _objectSpread({}, newItem);
    });
    if (!isEqual(prevItems, newItems)) {
      this.items = newItems;
      ReactDOM.render( /*#__PURE__*/React.createElement(MenuList, {
        ref: this.menuRef,
        items: this.items,
        view: this.view
      }), this.wrapperEl);
    }
  }
  destroy() {
    ReactDOM.unmountComponentAtNode(this.wrapperEl);
  }
}
export default function menuPlugin(editorId, items) {
  return new Plugin({
    view(view) {
      const menuId = "tap-editor-menu-".concat(editorId);
      let menuWrapperEl = document.getElementById(menuId);
      if (!menuWrapperEl) {
        menuWrapperEl = document.createElement('div');
        menuWrapperEl.classList.add('tap-editor-menuwrapper');
        menuWrapperEl.setAttribute('id', menuId);
        view.dom.parentNode.insertBefore(menuWrapperEl, view.dom);
      }
      const menuView = new MenuView(menuWrapperEl, items, view);
      return menuView;
    }
  });
}